import React, {useCallback, useState, useContext, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {NavLink} from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {contentIcClear24px} from '@ey/motif-react-wrapper/assets/icons/index';
import {
	relateToRiskRationaleMaxCharacters,
	specialOptionsForRelateToRiskDropdown,
	riskFactorRelationPatchDataType,
	clientSideEventNames
} from '../../../../../../../../util/uiconstants';
import {buildJsonPatchData} from '../../../../../../../../util/utils';
import Utility from '../../../../../../../../util/utilityfunctions';
import {updateRiskFactorRelation} from '../../../../../../../../actions/RiskFactor/riskfactoractions';
import {getRisks} from '../../../../../../../../actions/Risk/riskactions';
import {getRiskFactorsSlice, getRisksSlice} from '../../../../../../../../selectors/ISA315/selectors';
import {useIsDrawerMode} from '../../../../../../../../util/Ares/customHooks';
import EllipseControl from '../../../../../../EllipsesControl/EllipsesControl';
import CanvasFormTextArea from '../../../../../../CanvasForm/CanvasFormTextArea';
import GrayFadeProgressBar from '../../../../../../GrayFadeProgressBar/GrayFadeProgressBar';
import {useOverrideLabels} from '../../../../../../../Main/Forms/Utilities/OverrideLabelsProvider';
import RelatedRisksDropdown from './RelatedRisksDropdown';
import {FormBodyChangesContext} from '../../../../FormBodyChangesWatcher';
import RelatedRisksDescriptionDropdown from './RelatedRisksDescriptionDropdown';
import InlineMessage from '../../../../../../BaseComponentWrappers/MotifInlineMessage';
import ProgressBar from '../../../../../../BaseComponentWrappers/MotifProgressBar';
import Button from '../../../../../../BaseComponentWrappers/MotifButton';
import {useDocumentId} from '../../../../../../../../util/customHooks';
import RiskModal from '../../../../../../RiskFactors/ManageRisks/RiskModal';

export default function RiskFactorListItem({engagementId, headerId, bodyId, riskFactorId, isDisabled}) {
	const {labels} = useOverrideLabels();

	const isDrawerMode = useIsDrawerMode();
	let queryParameters = isDrawerMode ? {isDrawer: true} : {};
	const uniqueId = `${engagementId}-${headerId}-${bodyId}-${riskFactorId}`;
	const riskFactor = useSelector((state) => getRiskFactorsSlice(state)?.byId[riskFactorId]);
	const risksById = useSelector((state) => getRisksSlice(state)?.byId);

	const initialRationaleText = riskFactor.rationale ?? '',
		[rationaleValue, setRationaleValue] = useState(initialRationaleText),
		[count, setCount] = useState(initialRationaleText ? initialRationaleText.length : 0);

	const {onBodyValueChanged, resetChanges} = useContext(FormBodyChangesContext),
		documentId = useDocumentId();

	const rationaleInputName = `rationale-${riskFactor?.id}`;

	const [showRationaleText, setShowRationaleText] = useState(riskFactor?.isNotMaterial),
		[showRiskModal, setShowRiskModal] = useState(false),
		[showRationleError, setShowRationleError] = useState(false);

	const dispatch = useDispatch();
	const [isCleared, setIsCleared] = useState(false);

	let riskFactorUrl;
	let relatedRiskId;
	if (riskFactor) {
		riskFactorUrl = Utility.getAresTaskListNavigationUrl(
			riskFactor.documentId,
			riskFactor.formHeaderId,
			riskFactor.formSectionId,
			engagementId,
			riskFactor.taskId,
			{
				...queryParameters,
				documentid: riskFactor.documentId
			},
			riskFactor.documentTypeId
		);

		if (riskFactor.isNotMaterial || showRationaleText) {
			relatedRiskId = specialOptionsForRelateToRiskDropdown.notAROMM;
		} else if (riskFactor.riskId && risksById && Object.hasOwn(risksById, riskFactor.riskId)) {
			relatedRiskId = riskFactor.riskId;
		}
	}

	const relatedRisk = risksById && risksById[relatedRiskId];

	// RiskType dropdown option
	//	- riskFactor.isNotMaterial === true -> NotARomm
	//	- riskFactor.isNotMaterial === false -> riskFactor.risk.riskTypeId
	const initialRiskType = riskFactor?.isNotMaterial
		? specialOptionsForRelateToRiskDropdown.notAROMM
		: relatedRisk?.riskTypeId;

	const [riskType, setRiskType] = useState(initialRiskType);

	const showClearRiskIcon = riskType && riskType === relatedRisk?.riskTypeId;

	const customDescription = riskFactor && riskFactor.customDescription?.trim();
	const [disabled, setDisabled] = useState(!(relatedRiskId || riskType) || isDisabled);

	const onRiskTypeChange = (riskTypeOption) => {
		setRiskType(riskTypeOption.value);

		if (relatedRisk?.riskTypeId !== riskTypeOption.value) {
			onBodyValueChanged(bodyId, undefined, riskTypeOption.value, uniqueId);
		} else {
			resetChanges(bodyId, uniqueId);
		}

		setShowRationleError(!rationaleValue.length > 0);
	};

	const onRiskChange = useCallback(
		(riskOption, setIsLoading) => {
			let relationPatchData;
			let promise;
			setIsLoading(true);
			if (!riskOption) {
				relationPatchData = buildJsonPatchData(riskFactorRelationPatchDataType.riskId, null);
				relationPatchData = buildJsonPatchData(
					riskFactorRelationPatchDataType.documentId,
					documentId,
					relationPatchData
				);
				relationPatchData = buildJsonPatchData(riskFactorRelationPatchDataType.isNotMaterial, false, relationPatchData);
				setShowRationaleText(false);
				setIsCleared(true);
			} else {
				const isNotARommOption = riskOption.value === specialOptionsForRelateToRiskDropdown.notAROMM,
					isNoRisksIdentified = riskOption.value === specialOptionsForRelateToRiskDropdown.noRisksIdentified;

				setRiskType(riskOption.data?.riskTypeId);

				setShowRationaleText((prevShowRationale) => {
					// If this state changes from true to false, reset changes indicator for the risk factor
					if (prevShowRationale && !isNotARommOption) {
						resetChanges(bodyId, rationaleInputName);
					}
					return isNotARommOption;
				});

				if (!isNotARommOption && !isNoRisksIdentified) {
					// Selected a risk on the dropdown
					relationPatchData = buildJsonPatchData(riskFactorRelationPatchDataType.riskId, riskOption.value);
					relationPatchData = buildJsonPatchData(
						riskFactorRelationPatchDataType.documentId,
						documentId,
						relationPatchData
					);
				} else if (
					(isNotARommOption &&
						riskFactor?.rationale &&
						riskFactor.rationale.trim().length > 0 &&
						!riskFactor.isNotMaterial) ||
					(riskFactor.isNotMaterial && riskFactor.rationale.trim().length > 0)
				) {
					relationPatchData = buildJsonPatchData(riskFactorRelationPatchDataType.rationale, rationaleValue.trim());
					relationPatchData = buildJsonPatchData(
						riskFactorRelationPatchDataType.isNotMaterial,
						true,
						relationPatchData
					);
					relationPatchData = buildJsonPatchData(
						riskFactorRelationPatchDataType.documentId,
						documentId,
						relationPatchData
					);
					if (!rationaleValue) {
						setRationaleValue(riskFactor.rationale?.trim());
					}
				}
			}
			if (relationPatchData) {
				promise = dispatch(
					updateRiskFactorRelation(engagementId, headerId, riskFactor?.riskFactorID, riskFactor.id, relationPatchData)
				).then(() => {
					setIsLoading(false);
					resetChanges(bodyId, uniqueId);
					resetChanges(bodyId, rationaleInputName);
					window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
				});
			} else {
				promise = promise = Promise.resolve();
			}
			return promise.finally(() => {
				setIsLoading(false);
			});
		},
		[dispatch, engagementId, headerId, riskFactor, documentId, rationaleValue]
	);

	const onNewRiskClick = useCallback(() => {
		setShowRiskModal(true);
	}, []);

	const handlerOnModalClose = () => {
		setShowRiskModal(false);
		setTimeout(() => {
			dispatch(getRisks(engagementId, {pageSize: -1})).then(() => {
				window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
			});
		}, 1000);
	};

	const onRationalEditSave = useCallback(
		(rationaleText) => {
			let promise;
			if (rationaleText && rationaleText.trim().length > 0) {
				let relationPatchData = buildJsonPatchData(riskFactorRelationPatchDataType.rationale, rationaleText?.trim());
				relationPatchData = buildJsonPatchData(riskFactorRelationPatchDataType.isNotMaterial, true, relationPatchData);
				relationPatchData = buildJsonPatchData(
					riskFactorRelationPatchDataType.documentId,
					documentId,
					relationPatchData
				);

				promise = dispatch(
					updateRiskFactorRelation(engagementId, headerId, riskFactor?.riskFactorID, riskFactor.id, relationPatchData)
				);
			} else {
				promise = Promise.resolve();
			}
			promise = promise.then(() => {
				resetChanges(bodyId, rationaleInputName);
				resetChanges(bodyId, uniqueId);
			});
			setRationaleValue(rationaleText);

			return promise;
		},
		[dispatch, resetChanges, engagementId, headerId, riskFactor, documentId]
	);

	const onRationaleTextChange = useCallback(
		(evt) => {
			const rationaleText = evt.target?.value || evt.currentTarget.value;
			if (rationaleText.length > 0) {
				setShowRationleError(false);
			} else {
				setShowRationleError(true);
			}

			setCount(rationaleText.length);
			setRationaleValue(rationaleText);

			onBodyValueChanged(bodyId, initialRationaleText, rationaleText, rationaleInputName);
		},
		[onBodyValueChanged]
	);

	const onRationaleCancel = useCallback(() => {
		setShowRationleError(false);
		resetChanges(bodyId, rationaleInputName);
		setRationaleValue(initialRationaleText);
		setCount(initialRationaleText ? initialRationaleText.length : 0);
	}, [resetChanges, initialRationaleText]);

	const getRationaleValue = () => {
		if (rationaleValue) {
			return Utility.getHtmlTextElement(rationaleValue);
		}
		return '';
	};

	useEffect(() => {
		setShowRationleError(!rationaleValue.length > 0);
	}, [riskType]);

	return (
		<React.Fragment>
			{riskFactor && (
				<GrayFadeProgressBar
					customLoader={<ProgressBar hideLabel className={`greyOutArea `} circle isIndeterminate />}
					children={(setIsLoading) => {
						return (
							<StyledRiskFactorListItem className="StyledRiskFactorListItem">
								<section className="risk-factor-name-container">
									<section className="column-risk-factor">{labels.riskFactor}</section>
									<NavLink to={riskFactorUrl} className="risk-factor-link">
										<EllipseControl
											content={riskFactor.riskFactorName}
											tooltip={riskFactor.riskFactorName}
											noOfLines={2}
											tooltipClass="tooltipCustomWidth"
										/>
									</NavLink>
									{/* Custom description */}
									{customDescription?.length > 0 && (
										<section className="riskfactor-custom-description">
											<EllipseControl
												content={customDescription}
												tooltip={customDescription}
												noOfLines={3}
												tooltipClass="tooltipCustomWidth"
											/>
										</section>
									)}
								</section>
								<section className={`risk-desc-container ${showRationaleText ? 'long-desc-container' : ''}`}>
									<section className="related-risk-container-wrapper">
										<section className="column-relate-riskfactor">{labels.significanceLabel}</section>
										<section className="related-risk-container">
											<RelatedRisksDropdown
												selectedRiskId={relatedRiskId}
												riskType={riskType}
												setRiskType={setRiskType}
												isDisabled={isDisabled}
												setDisabled={setDisabled}
												setShowRationaleText={setShowRationaleText}
												onRiskTypeChange={(riskTypeOption) => onRiskTypeChange(riskTypeOption)}
												onRiskChange={(riskOption) => onRiskChange(riskOption, setIsLoading)}
												isCleared={isCleared}
												setIsCleared={setIsCleared}
												knowledgeTagIds={riskFactor?.knowledgeTagIds}
											/>
										</section>
									</section>
									{!showRationaleText && (
										<section className="risk-type-container-wrapper">
											<section className="column-risk-type">{labels.description}</section>
											<section className="risk-type-container">
												<RelatedRisksDescriptionDropdown
													selectedRiskId={relatedRiskId}
													riskTypeId={riskType}
													onRiskChange={(riskOption) => onRiskChange(riskOption, setIsLoading)}
													isDisabled={disabled || isDisabled}
													onNewRiskClick={onNewRiskClick}
													riskFactor={riskFactor}
													isCleared={isCleared}
													showClearIcon
												/>
											</section>
										</section>
									)}
									{showRationaleText && (
										<>
											<section className="line-break" />
											<section className="rationale-textarea-container text-area">
												<section className="column-risk-type">{labels.description}</section>

												<section className="rationale-textarea-wrapper">
													<CanvasFormTextArea
														id={riskFactor?.id}
														maxLength={relateToRiskRationaleMaxCharacters}
														showLabel={false}
														label={labels.describeRationale}
														value={getRationaleValue()}
														onAccept={onRationalEditSave}
														onChange={onRationaleTextChange}
														onCancel={onRationaleCancel}
														onRiskChange={(riskOption) => onRiskChange(riskOption, setIsLoading)}
														textAreaErrorMessage={labels.provideRationale}
														isAresView
														showPlainText
														showRightCounter
														count={count}
														relateToRiskRationaleMaxCharacters={relateToRiskRationaleMaxCharacters}
														isReadOnly={isDisabled}
													/>
													{rationaleValue.length > 0 && !isDisabled && (
														<Button
															className={'not-romm-clear-button'}
															variant="text"
															showIcon
															iconSource={contentIcClear24px}
															onClick={() => onRiskChange(null, setIsLoading)}
															showLabel
															buttonLabel={labels.clear}
															labelClassName={'clearText'}
															title={labels.clearSignificance}
														/>
													)}
												</section>
												{/* <span className="pull-right-counter">{`${count}/${relateToRiskRationaleMaxCharacters}`}</span> */}
											</section>
											<section className="rationale-textarea-container warning-msg">
												{showRationleError && (
													<InlineMessage
														className={'inline-message'}
														variant="warning"
														children={<span className="error-message">{labels.provideRationale}</span>}
													/>
												)}
											</section>
										</>
									)}
								</section>
								{showRiskModal && (
									<RiskModal
										show
										headerId={headerId}
										riskFactorId={riskFactorId}
										engagementId={engagementId}
										documentId={documentId}
										riskFactorRelationId={riskFactor?.riskFactorID}
										riskType={riskType}
										onClose={handlerOnModalClose}
										onSaveSuccess={() => {
											resetChanges(bodyId, uniqueId);
										}}
									/>
								)}
							</StyledRiskFactorListItem>
						);
					}}
				/>
			)}
		</React.Fragment>
	);
}

RiskFactorListItem.propTypes = {
	engagementId: PropTypes.number,
	documentId: PropTypes.string,
	headerId: PropTypes.number,
	bodyId: PropTypes.number,
	riskFactorId: PropTypes.number
};

const StyledRiskFactorListItem = styled.section`
	display: flex;
	flex-wrap: wrap;
	padding: var(--px-25) 0;
	border-bottom: var(--px-1) solid var(--neutrals-200);
	.risk-desc-container {
		width: 70%;
		display: flex;
		.related-risk-container-wrapper {
			display: flex;
			flex-direction: column;
			width: 30%;
			.column-relate-riskfactor {
				font-size: var(--px-14);
				font-weight: 400;
				line-height: var(--px-20);
				color: var(--neutrals-900);
				margin-top: var(--px-8);
				margin-bottom: var(--px-4);
			}
		}
		.risk-type-container-wrapper {
			display: flex;
			flex-direction: column;
			width: 70%;
			.column-risk-type {
				font-size: var(--px-14);
				font-weight: 400;
				line-height: var(--px-20);
				color: var(--neutrals-900);
				margin-top: var(--px-8);
				margin-bottom: var(--px-4);
			}
		}
	}
	&:last-child {
		border-bottom: none;
	}
	.risk-factor-container-loader {
		width: 100%;
		padding: var(--px-20);
	}
	.risk-factor-name-container {
		width: 30%;
		padding-right: var(--px-24);
		font-size: var(--px-14);
		.column-risk-factor {
			font-size: var(--px-16);
			font-weight: 400;
			line-height: var(--px-24);
			color: var(--neutrals-900);
			margin-top: var(--px-8);
			margin-bottom: var(--px-12);
		}
		.risk-factor-link {
			display: block;
			max-width: 100%;
			font-weight: 400;
			border: none;
			font-size: var(--px-14);
			color: var(--blue-600);
			line-height: var(--px-20);
			text-decoration: none;
			&:hover {
				color: var(--blue-600);
				text-decoration: underline;
			}
		}
		.riskfactor-custom-description {
			display: block;
			width: 100%;
			font-weight: 300;
			font-size: var(--px-14);
			line-height: var(--px-20);
			color: var(--neutrals-700);
		}
	}
	.related-risk-container {
		width: 100%;
		padding-right: var(--px-24);
		.motif-inline-message {
			margin-top: var(--px-5);
		}
		.react-select-container {
			.react-select__option {
				font-size: var(--px-16) !important;
				font-weight: 300 !important;
				line-height: var(--px-24) !important;
				color: var(--neutrals-900) !important;
			}
		}
	}
	.risk-type-container {
		width: 100%;
		.motif-inline-message {
			margin-top: var(--px-5);
			.motif-inline-message-content svg {
				fill: var(--red-600);
				cursor: inherit;
			}
		}
		.react-select-container {
			.react-select__option {
				font-size: var(--px-16) !important;
				font-weight: 300 !important;
				line-height: var(--px-24) !important;
				color: var(--neutrals-900) !important;
			}
		}
	}
	.rationale-textarea-container {
		display: flex;
		margin-left: 50%;
		width: 50%;
		margin-top: var(--px-5);
		.motif-inline-message {
			&:hover {
				cursor: unset;
			}
		}
		.motif-inline-message-content svg {
			fill: var(--red-600);
			cursor: inherit;
		}
		.motif-inline-message-warning {
			.motif-inline-message-content svg {
				fill: var(--orange-600);
			}
		}
		&.text-area {
			margin-top: var(--px-25);
		}
		.canvas-form-textarea {
			width: 90%;
			.motif-form-field > .motif-label {
				font-size: var(--px-12) !important;
			}
			.StyledCanvasFormInputDisplay {
				border: unset;
				border: var(--px-1) solid var(--neutrals-300);
				.motif-label {
					font-size: var(--px-12) !important;
					left: var(--px-18) !important;
				}
			}
		}
		.pull-right-counter {
			margin: 0;
			text-align: right;
			font-size: var(--px-12);
			font-weight: 300;
			line-height: var(--px-18);
			color: var(--neutrals-700) !important;
			width: 100% !important;
		}
		.rationale-textarea-wrapper {
			display: flex;
			.canvas-form-textarea {
				height: var(--px-94);
				width: calc(100% - var(--px-90)) !important;
			}
			.not-romm-clear-button {
				width: auto;
				max-width: var(--px-80);
				margin-left: var(--px-10);
				justify-content: flex-end;
				align-self: flex-end;
				.motif-icon {
					display: flex;
					align-items: center;
					height: var(--px-20);
					margin-right: var(--px-2);
					svg {
						width: var(--px-20) !important;
						height: var(--px-20) !important;
					}
				}
				.clearText {
					font-weight: 700;
					font-size: var(--px-14);
					line-height: var(--px-22);
				}
			}
		}
	}
`;
